import { createSelector } from 'reselect';
import { getLoadingStatuses } from 'helpers/selectors';


const getSites = ({ generalFilter }) => {
    return generalFilter.sites?.data ? generalFilter.sites.data.map((site) => {
        return {
            siteId: site.siteID,
            siteName: site.siteName
        };
    }) : [];
};

const getSelectedSites = ({ generalFilter }) => {
    return generalFilter.selectedFilters.sitesChecked ?
        generalFilter.selectedFilters.sites.map((site) => {
            return {
                siteId: site.siteID,
                siteName: site.siteName
            };
        }) :
        [];
};

const getOrganizations = ({ generalFilter }) => {
    return generalFilter.selectedFilters.organizations || [];
};

const getSearchboxFilter = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.searchboxFilter;
};

const getSelectedPowertrain = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.selectedPowertrain;
};

const getPowertrains = ({ powertrainTools }) => {
    return powertrainTools.configurationV2?.powertrains?.data;
};

const getPowertrainsLoadingStatus = ({ powertrainTools }) => {
    return powertrainTools.configurationV2?.powertrains?.loadingStatus;
};

const getPowertrainsCurrentPage = ({ powertrainTools }) => {
    return powertrainTools.configurationV2?.powertrains?.pageNumber;
};

const getHasMorePowertrains = ({ powertrainTools }) => {
    return powertrainTools.configurationV2?.powertrains?.hasMore;
};

// Wizard loading states
const getIsAddingPowertrain = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.isAddingPowertrain || false;
};

const getIsEditingPowertrain = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.isEditingPowertrain || false;
};

const getPowertrainOperationError = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.powertrainOperationError;
};

const getHierarchyData = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.hierarchyData?.data?.hierarchyData || {};
};

const getHierarchyDataLoadingStatus = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.hierarchyData?.loadingStatus;
};

const getUpdateHierarchyLoadingStatus = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.updateHierarchyLoadingStatus;
};

const getSelectedHierarchyNodes = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.selectedHierarchyNodes || [];
};

const getHierarchyExpandedNodes = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.hierarchyExpandedNodes || [];
};

const getIsEditHierarchy = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.isEditHierarchy || false;
};

const getHierarchyRequestData = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.hierarchyRequestData;
};

const getHierarchySelectedAssets = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.hierarchySelectedAssets || [];
};

const getHierarchyErrorModalVisible = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.hierarchyErrorModalVisible || false;
};

const getIsSaveFailed = ({ powertrainTools }) => {
    return powertrainTools.configurationV2.isSaveFailed || false;
};

const getIsPartialPowertrainFailure = ({ powertrainTools }) => {
    return powertrainTools?.configurationV2?.isPartialPowertrainFailure || false;
};

const getConversionResult = ({ powertrainTools }) => {
    return powertrainTools?.configurationV2?.conversionResult;
};

export const getModel = createSelector(
    [
        getSites,
        getSelectedSites,
        getOrganizations,
        getLoadingStatuses,
        getSelectedPowertrain,
        getSearchboxFilter,
        getPowertrains,
        getPowertrainsLoadingStatus,
        getPowertrainsCurrentPage,
        getHasMorePowertrains,
        getIsAddingPowertrain,
        getIsEditingPowertrain,
        getPowertrainOperationError,
        getHierarchyData,
        getHierarchyDataLoadingStatus,
        getUpdateHierarchyLoadingStatus,
        getSelectedHierarchyNodes,
        getHierarchyExpandedNodes,
        getIsEditHierarchy,
        getHierarchyRequestData,
        getHierarchySelectedAssets,
        getHierarchyErrorModalVisible,
        getIsSaveFailed,
        getIsPartialPowertrainFailure,
        getConversionResult
    ],
    (
        sites,
        selectedSites,
        organizations,
        generalFilterLoadingStatuses,
        selectedPowertrain,
        searchboxFilter,
        filteredPowertrains,
        powertrainsLoadingStatus,
        powertrainsCurrentPage,
        hasMorePowertrains,
        isAddingPowertrain,
        isEditingPowertrain,
        powertrainOperationError,
        hierarchyData,
        hierarchyDataLoadingStatus,
        updateHierarchyLoadingStatus,
        selectedHierarchyNodes,
        hierarchyExpandedNodes,
        isEditHierarchy,
        hierarchyRequestData,
        hierarchySelectedAssets,
        hierarchyErrorModalVisible,
        isSaveFailed,
        isPartialPowertrainFailure,
        conversionResult
    ) => {
        // Don't add powertrain operation status to LoadingStatusWrapper
        // The wizard has its own internal loading UI and needs the parent component
        // to stay mounted so it can react to success/failure states
        const allLoadingStatuses = [...generalFilterLoadingStatuses];

        return {
            sites,
            selectedSites,
            organizations,
            generalFilterLoadingStatuses: allLoadingStatuses,
            selectedPowertrain,
            searchboxFilter,
            filteredPowertrains,
            powertrainsLoadingStatus,
            powertrainsCurrentPage,
            hasMorePowertrains,
            isAddingPowertrain,
            isEditingPowertrain,
            powertrainOperationError,
            hierarchyData,
            hierarchyDataLoadingStatus,
            updateHierarchyLoadingStatus,
            selectedHierarchyNodes,
            hierarchyExpandedNodes,
            isEditHierarchy,
            hierarchyRequestData,
            hierarchySelectedAssets,
            hierarchyErrorModalVisible,
            isSaveFailed,
            isPartialPowertrainFailure,
            conversionResult
        };
    }
);
